import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

import {
  IActiveLearningIssue,
  IActiveLearningAnswers,
} from "../../../../../state";
import { Context } from "../../../../../studentStore";
import { IAnswer } from "../../../../../constants/active_learning";
import Button from "../../../../../atoms/ActiveLearning/Button";
import Selector from "../../../../../atoms/ActiveLearning/Selector";
import TextArea from "../../../../../atoms/ActiveLearning/TextArea";
import CommentList from "../../../../../molecules/ActiveLearning/CommentList";
import ActiveLearningModalConfirm from "../../../../../molecules/ActiveLearningModalConfirm";
import styles from "../../ActiveLearningIssue.module.scss";
import hintImage13 from "../../../../../assets/images/ActiveLearning/hint/img_13.webp";

interface IActiveLearningIssue1Props {
  issue?: IActiveLearningIssue;
  answers: IActiveLearningAnswers[];
  setAnswers: Dispatch<SetStateAction<IActiveLearningAnswers[]>>;
  saveAnswers: () => void;
}

export interface ICurrentAnswer extends IAnswer {
  from_student_id: number;
  to_student_id: number;
  value: string;
}

export const Issue2 = (props: IActiveLearningIssue1Props) => {
  const { state } = React.useContext(Context);
  const currentIssueNo = 2;
  const question1 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 1);
  }, [props.issue?.questions]);

  const [isShowModalConfirm, setIsShowModalConfirm] = useState(false);
  const [studentId, setStudentId] = useState(0);

  const [answerToQ1, setAnswerToQ1] = useState<ICurrentAnswer>({
    id: null,
    issue_no: currentIssueNo,
    question_no: question1?.order_num || NaN,
    question_id: question1?.id || NaN,
    from_student_id: state.student?.id || NaN,
    to_student_id: NaN,
    value: "",
  });

  const syncStoreAnswerToState = useCallback(
    (
      questionNo: number,
      setter: Dispatch<React.SetStateAction<ICurrentAnswer>>,
      toStudentId: number,
    ) => {
      const targetAnswer = state.active_learning_answers.find(
        answer =>
          answer.issue_no === currentIssueNo &&
          answer.question_no === questionNo &&
          answer.contents?.to_student_id === toStudentId,
      );
      const answer: ICurrentAnswer = {
        id: targetAnswer?.id || null,
        issue_no: currentIssueNo,
        question_no: questionNo,
        question_id: targetAnswer?.question_id || question1?.id || 0,
        from_student_id: state.student?.id || NaN,
        to_student_id: toStudentId,
        value: targetAnswer?.contents?.value || "",
      };
      setter({ ...answer });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.active_learning_answers],
  );

  useEffect(() => {
    // Store側のデータを各ステートに同期
    syncStoreAnswerToState(
      question1?.order_num || NaN,
      setAnswerToQ1,
      answerToQ1.to_student_id,
    );
  }, [
    state.active_learning_answers,
    syncStoreAnswerToState,
    question1,
    answerToQ1.to_student_id,
  ]);

  useEffect(() => {
    const excludedAnswer = props.answers.filter(
      answer => answer.issue_no !== currentIssueNo,
    );
    const currentAnswers: IActiveLearningAnswers[] = [
      {
        id: answerToQ1.id,
        issue_no: answerToQ1.issue_no,
        question_no: answerToQ1.question_no,
        question_id: answerToQ1.question_id,
        contents: {
          from_student_id: answerToQ1.from_student_id,
          to_student_id: answerToQ1.to_student_id,
          value: answerToQ1.value,
        },
      },
    ];
    props.setAnswers([...excludedAnswer, ...currentAnswers]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerToQ1, props.setAnswers]);

  const isEditingValue = !!state.active_learning_answers.find(
    answer =>
      answer.issue_no === currentIssueNo &&
      answer.question_no === question1?.order_num &&
      answer.contents?.to_student_id === answerToQ1.to_student_id &&
      answer.contents?.value,
  );

  const answersI2Q1 = state.active_learning_answers.filter(
    answer => answer.issue_no === currentIssueNo && answer.question_no === 1,
  );

  const submittedAnswers = answersI2Q1.map(answer => {
    const matchedStudent = state.students.find(
      student => student.id === answer.contents.to_student_id,
    );

    return {
      student_id: matchedStudent?.id,
      full_name: `${matchedStudent?.last_name} ${matchedStudent?.first_name}`,
      contents: answer.contents,
    };
  });

  const matchedSubmitAnswer = submittedAnswers.find(
    answer => answer.student_id === answerToQ1.to_student_id,
  );

  const showModalIfAnswerDifferent = (nextStudentId: number) => {
    if (matchedSubmitAnswer) {
      const isDifferentAnswer =
        matchedSubmitAnswer.contents.value !== answerToQ1.value;
      if (isDifferentAnswer) {
        setStudentId(nextStudentId);
        setIsShowModalConfirm(true);
      } else {
        setAnswerToQ1({
          ...answerToQ1,
          to_student_id: nextStudentId,
        });
      }
    } else if (answerToQ1.value.length > 0) {
      setStudentId(nextStudentId);
      setIsShowModalConfirm(true);
    } else {
      setAnswerToQ1({
        ...answerToQ1,
        to_student_id: nextStudentId,
      });
    }
  };

  return (
    <div className={styles.issue}>
      {isShowModalConfirm && (
        <ActiveLearningModalConfirm
          onClose={() => setIsShowModalConfirm(false)}
          title={
            "入力された内容はまだ保存されていません。保存せずに生徒を切り替えると、入力内容が失われます。よろしいですか？"
          }
          buttons={[
            {
              title: "いいえ",
              action: () => {
                setIsShowModalConfirm(false);
              },
              type: "None",
            },
            {
              title: "はい",
              action: () => {
                setAnswerToQ1({
                  ...answerToQ1,
                  to_student_id: studentId,
                });
                setIsShowModalConfirm(false);
              },
              type: "Success",
            },
          ]}
        />
      )}
      <div className={styles.header}>
        <span className={styles.no}>
          WORK
          {props.issue?.no && props.issue?.no < 10
            ? `0${props.issue?.no}`
            : props.issue?.no}
        </span>
        <p className={styles.title}>{props.issue?.title}</p>
        <p className={styles.read}>
          これまで考えてきた「問い」/「課題」/解決のアイデアを相手に伝わりやすくまとめて発表しよう。
          <br />
          発表を聞き終わったら、発表した人に良かった点を伝えたり、アドバイスしたりしよう。
        </p>
      </div>
      <img src={hintImage13} alt="発表を聞くときに良かった点を探すヒント" />
      {question1 && (
        <>
          <div className={styles.themEntry}>
            <p className={styles.title}>{question1?.title}</p>
            <p className={styles.read}>
              発表を聞いて良かった点やアドバイスを具体的に入力して、発表者に送信しよう。
            </p>
          </div>
          <div className={styles.tree}>
            <Selector
              selectedValue={answerToQ1.to_student_id}
              handleChange={e => {
                showModalIfAnswerDifferent(parseInt(e.target.value));
              }}
              options={state.students
                .filter(student => student.id !== state.student?.id)
                .map(student => ({
                  value: student.id,
                  label: `${student.last_name} ${student.first_name}`,
                }))}
            />
            <ul className={styles.branchList}>
              <li className={styles.branch}>
                <div className={styles.labelWrapper}>
                  <span className={styles.label}>コメント</span>
                </div>
                <div className={styles.inputField}>
                  <TextArea
                    value={answerToQ1.value}
                    handleChange={e =>
                      setAnswerToQ1({
                        ...answerToQ1,
                        value: e.target.value,
                      })
                    }
                  />
                </div>
              </li>
            </ul>
            <div className={styles.buttonWrapper}>
              <Button
                width={"158px"}
                height={"38px"}
                fontSize={"14px"}
                handleClick={props.saveAnswers}
                disabled={Number.isNaN(answerToQ1.to_student_id)}
              >
                {isEditingValue ? "コメントを上書き" : "送信する"}
              </Button>
            </div>
          </div>
          <div className={styles.commentList}>
            <div className={styles.label}>
              <p className={styles.title}>送ったコメント</p>
            </div>
            <CommentList commentList={submittedAnswers} />
          </div>
        </>
      )}
    </div>
  );
};
export default Issue2;
